import AutoFixHighRoundedIcon from '@mui/icons-material/AutoFixHighRounded';
import ConstructionRoundedIcon from '@mui/icons-material/ConstructionRounded';
import QueryStatsRoundedIcon from '@mui/icons-material/QueryStatsRounded';
import SettingsSuggestRoundedIcon from '@mui/icons-material/SettingsSuggestRounded';
import SupportAgentRoundedIcon from '@mui/icons-material/SupportAgentRounded';
import ThumbUpAltRoundedIcon from '@mui/icons-material/ThumbUpAltRounded';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import * as React from 'react';

import { AMBER, FONT_BODY, FONT_DISPLAY, Reveal, SectionHeading, TEAL } from './LandingKit';

const items = [
  {
    icon: <ConstructionRoundedIcon />,
    title: 'Sem hardware',
    description:
      'Nada de rastreador para comprar, instalar ou manter. O celular do motorista já faz o trabalho.',
  },
  {
    icon: <QueryStatsRoundedIcon />,
    title: 'Em tempo real',
    description:
      'Posições e status de coleta atualizam ao vivo, não no fim do dia, quando já é tarde para agir.',
  },
  {
    icon: <ThumbUpAltRoundedIcon />,
    title: 'Pronto para usar',
    description:
      'Sua equipe começa em minutos. Sem treinamento, sem implantação longa.',
  },
  {
    icon: <AutoFixHighRoundedIcon />,
    title: 'BI no WhatsApp',
    description:
      'Pergunte como está a operação e receba a resposta com dado real, na hora, sem abrir o sistema.',
  },
  {
    icon: <SupportAgentRoundedIcon />,
    title: 'Suporte de gente',
    description:
      'Você fala com quem conhece a operação, por email e WhatsApp, sem fila de robô.',
  },
  {
    icon: <SettingsSuggestRoundedIcon />,
    title: 'Dados protegidos',
    description:
      'Tudo criptografado em trânsito e em repouso, armazenado em servidores seguros.',
  },
];

export default function Highlights() {
  return (
    <Box
      id="highlights"
      sx={{
        position: 'relative',
        overflow: 'hidden',
        pt: { xs: 10, sm: 16 },
        pb: { xs: 10, sm: 18 },
        color: '#f2fbf9',
        backgroundColor: '#05080b',
      }}
    >
      {/* control-room glow: teal + amber on the dark band */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background:
            `radial-gradient(50% 40% at 80% 8%, ${TEAL}26, transparent 70%),` +
            `radial-gradient(42% 38% at 18% 98%, ${AMBER}1f, transparent 72%)`,
        }}
      />
      {/* technical grid (always dark variant here) */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.045) 1px, transparent 1px),' +
            'linear-gradient(90deg, rgba(255,255,255,0.045) 1px, transparent 1px)',
          backgroundSize: '54px 54px',
          maskImage: 'radial-gradient(circle at 50% 30%, black, transparent 80%)',
          WebkitMaskImage: 'radial-gradient(circle at 50% 30%, black, transparent 80%)',
        }}
      />

      <Container
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: { xs: 5, sm: 8 },
        }}
      >
        <Reveal>
          <SectionHeading
            overline="POR QUE DRIVESYNC"
            title="Por que transportadoras escolhem o DriveSync"
            subtitle="Controle de coletas em tempo real, sem o custo e a dor do hardware. E a operação inteira no WhatsApp."
            align="center"
            maxWidth={880}
            onDark
          />
        </Reveal>

        <Grid container spacing={2.5} sx={{ width: '100%' }}>
          {items.map((item, index) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
              <Reveal delay={0.06 * index} sx={{ height: '100%' }}>
                <Stack
                  direction="column"
                  spacing={1.5}
                  useFlexGap
                  sx={{
                    color: 'inherit',
                    p: 3,
                    height: '100%',
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'rgba(255,255,255,0.10)',
                    backgroundColor: 'rgba(255,255,255,0.04)',
                    backdropFilter: 'blur(12px)',
                    boxShadow: '0 18px 44px rgba(0,0,0,0.4)',
                    transition:
                      'transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 26px 60px rgba(0,0,0,0.55)',
                      borderColor: `${TEAL}66`,
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: index % 2 ? AMBER : TEAL,
                      border: '1px solid',
                      borderColor: index % 2 ? `${AMBER}40` : `${TEAL}40`,
                      backgroundColor: index % 2 ? `${AMBER}14` : `${TEAL}14`,
                      '& svg': { fontSize: 24 },
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontFamily: FONT_DISPLAY,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '-0.005em',
                        fontSize: '1.05rem',
                        lineHeight: 1.15,
                        color: '#f2fbf9',
                        mb: 0.75,
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: FONT_BODY,
                        fontSize: '0.92rem',
                        lineHeight: 1.5,
                        color: '#9fc4bd',
                      }}
                    >
                      {item.description}
                    </Typography>
                  </Box>
                </Stack>
              </Reveal>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}
